import { useState } from 'react'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'

import { TApi } from '../../../../shared/const'
import http from '../../../../shared/http'
import useDialogDismiss from '../../../../shared/useDialogDismiss'
import useIsDirty from '../../../../shared/useIsDirty'
import { askDiscardChanges } from '../../../../shared/confirmDialog'
import { formatVienna } from '../../../../shared/datetime'

import Btn from '../../../../UI/Btn'
import { PlainInput, PlainTextarea } from '../../../../UI/PlainField'
import InvitationLinkShare from '../../../../UI/InvitationLinkShare'

import styles from './inviteModal.module.scss'

const EMPTY = {
	firstName: '',
	lastName: '',
	email: '',
	message: ''
}

/**
 * Eine Expert:in selbst einladen — durch Schüler:innen oder die Lehrkraft.
 *
 * Gefragt wird nur nach Name und E-Mail-Adresse. Firma, Funktion, Kompetenzen,
 * Zielgruppen und Kurzprofil trägt die eingeladene Person beim Aktivieren
 * selbst ein; sie weiß es besser als die Klasse, die sie einlädt — und sie
 * stimmt dabei selbst zu, statt dass jemand anderes Angaben über sie macht.
 * Firma und Funktion standen bis 09.08.2026 als Felder hier; erfasst wurden
 * damit Angaben über eine Person, die noch nichts davon wusste.
 *
 * Der Hinweis über dem Absenden sagt, was passiert: Die Person ist nach dem
 * Aktivieren sofort dabei — es gibt keine Freigabe durch Junior Achievement,
 * die einen Irrtum noch abfangen würde.
 */
export default function InviteExpertModal({ show, companies = [], onClose, onInvited }) {
	const { t } = useTranslation()

	const [values, setValues] = useState(EMPTY)
	// Bei genau einer Junior Company gibt es nichts zu entscheiden.
	const [companyId, setCompanyId] = useState(companies.length === 1 ? String(companies[0].id) : '')
	const [isSaving, setSaving] = useState(false)
	const [errors, setErrors] = useState({})
	// Nach dem Einladen bleibt das Fenster stehen und bietet den Link zum
	// Weitergeben an - die Mail von Junior Achievement landet manchmal im Spam,
	// und später kommt die Schule an den Link nicht mehr heran.
	const [sent, setSent] = useState(null)
	const senderName = useSelector(state =>
		[state.user?.profile?.firstName, state.user?.profile?.lastName].filter(Boolean).join(' ')
	)

	const isDirty = useIsDirty(show ? values : null)

	const requestClose = async () => {
		if (sent) {
			setSent(null)
			onInvited()
			return
		}
		if (isDirty && !(await askDiscardChanges())) return

		// Beim nächsten Öffnen soll ein leeres Formular dastehen und nicht das
		// eben Verworfene.
		setValues(EMPTY)
		setErrors({})
		onClose()
	}

	const backdropProps = useDialogDismiss(requestClose, { enabled: show })

	if (!show) return null

	const setValue = (key, value) => setValues(current => ({ ...current, [key]: value }))

	const chosen = companies.find(c => String(c.id) === String(companyId))

	const validate = () => {
		const next = {}
		if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(values.email)) next.email = t('Invalid email')
		if (values.firstName.trim().length < 2) next.firstName = t('Required field')
		if (values.lastName.trim().length < 2) next.lastName = t('Required field')
		if (companies.length > 1 && !companyId) next.companyId = t('Required field')

		setErrors(next)
		return !Object.keys(next).length
	}

	const submit = event => {
		event.preventDefault()
		if (!validate()) return

		setSaving(true)
		http.post(TApi.COMMON_SUPPORTER_INVITE, {
			...values,
			...(companyId ? { companyId: Number(companyId) } : {})
		})
			.then(({ data }) => {
				// Der Server meldet zurück, ob die E-Mail wirklich raus ist. Eine
				// angelegte Einladung ohne versendete Mail bliebe sonst unbemerkt —
				// und alle warten auf eine Antwort, die nie kommen kann.
				// War die Adresse schon von uns eingeladen, schickt der Server
				// dieselbe Einladung erneut (#248) und sagt, seit wann sie offen war.
				const resentSince = data?.resent ? formatVienna(data.previouslyInvitedAt, 'dd.MM.yyyy') : null
				if (data?.emailSent === false) {
					toast.warning(t('Invitation saved, but the email could not be sent'))
				} else {
					toast.success(resentSince ? t('expertInviteResent') : t('expertInvited'))
				}
				setSent({
					link: data?.activationLink || null,
					email: values.email.trim(),
					name: `${values.firstName.trim()} ${values.lastName.trim()}`,
					company: chosen?.name || companies[0]?.name || null,
					emailSent: data?.emailSent !== false,
					validDays: data?.validDays || 60,
					resentSince
				})
				setValues(EMPTY)
				// Ohne Link (älterer Server) gibt es nichts weiterzugeben.
				if (!data?.activationLink) {
					setSent(null)
					onInvited()
				}
			})
			.catch(() => {}) // Meldung kommt aus shared/http
			.finally(() => setSaving(false))
	}

	if (sent) {
		return (
			<div className={styles.overlay} {...backdropProps}>
				<div className={styles.modal}>
					<h2 className={styles.title}>{t(sent.resentSince ? 'expertInviteResent' : 'expertInvited')}</h2>
					{sent.resentSince && (
						<p className={styles.notice}>
							{t('inviteExpertAlreadyInvited', { name: sent.name, date: sent.resentSince })}
						</p>
					)}
					<p className={styles.hint}>
						{sent.emailSent
							? t('inviteExpertSentTo', { name: sent.name, email: sent.email })
							: t('inviteExpertNotSent', { name: sent.name })}
					</p>
					<InvitationLinkShare
						link={sent.link}
						email={sent.email}
						recipientName={sent.name}
						companyName={sent.company}
						senderName={senderName}
						validDays={sent.validDays}
					/>
					<div className={styles.footer}>
						<Btn type="button" onClick={requestClose}>
							{t('Done')}
						</Btn>
					</div>
				</div>
			</div>
		)
	}

	return (
		<div className={styles.overlay} {...backdropProps}>
			<form className={styles.modal} onSubmit={submit}>
				<h2 className={styles.title}>{t('inviteExpert')}</h2>
				<p className={styles.hint}>{t('inviteExpertHint')}</p>

				<div className={styles.grid}>
					<PlainInput
						label={t('firstName')}
						value={values.firstName}
						error={errors.firstName}
						onChange={e => setValue('firstName', e.target.value)}
					/>
					<PlainInput
						label={t('lastName')}
						value={values.lastName}
						error={errors.lastName}
						onChange={e => setValue('lastName', e.target.value)}
					/>
					<PlainInput
						label={t('email')}
						value={values.email}
						error={errors.email}
						onChange={e => setValue('email', e.target.value)}
					/>
				</div>

				{/*
				 * Eine Lehrkraft betreut mehrere Junior Companies. Die Einladung nennt
				 * die Firma im Text und bindet die Sichtbarkeit des Profils an sie —
				 * ohne diese Wahl ginge sie an eine beliebige.
				 */}
				{companies.length > 1 && (
					<div className={styles.block}>
						<span className={styles.blockLabel}>{t('inviteForCompany')}</span>
						<select
							className={styles.companySelect}
							value={companyId}
							onChange={e => setCompanyId(e.target.value)}
						>
							<option value="">{t('chooseCompany')}</option>
							{companies.map(c => (
								<option key={c.id} value={c.id}>
									{c.name}
								</option>
							))}
						</select>
						{errors.companyId && <span className={styles.error}>{errors.companyId}</span>}
					</div>
				)}

				<div className={styles.block}>
					<PlainTextarea
						label={t('inviteMessage')}
						rows={3}
						maxLength={1000}
						value={values.message}
						placeholder={t('inviteMessagePlaceholder')}
						onChange={e => setValue('message', e.target.value)}
					/>
				</div>

				<p className={styles.notice}>
					{t('inviteExpertNotice', { company: chosen?.name || companies[0]?.name || '' })}
				</p>

				<div className={styles.footer}>
					<Btn type="button" theme="secondary" onClick={requestClose}>
						{t('Cancel')}
					</Btn>
					<Btn type="submit" disabled={isSaving}>
						{t('sendInvitation')}
					</Btn>
				</div>
			</form>
		</div>
	)
}

InviteExpertModal.propTypes = {
	show: PropTypes.bool,
	companies: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, name: PropTypes.string })),
	onClose: PropTypes.func.isRequired,
	onInvited: PropTypes.func.isRequired
}
